// client/src/utils/currencyUtils.js
/**
 * =================================================================
 * Currency Utilities for Multi-Tenant Application
 * =================================================================
 * Curated list of currencies covering the regions whose languages
 * the app supports (see SUPPORTED_LANGUAGES in src/i18n/config.js).
 *
 * Capped at 15 entries per product requirement. EUR collapses the
 * four euro-zone locales (es-ES, fr-FR, de-DE, it-IT) into one
 * shared currency; CAD is included because Canadian English/French
 * is a common dialect even though there is no dedicated locale.
 * =================================================================
 */

// ISO 4217 code → human label + symbol + the language locales
// that primarily transact in this currency.
export const CURRENCIES = {
  USD: { code: 'USD', name: 'US Dollar',          symbol: '$',  locales: ['en-US'] },
  EUR: { code: 'EUR', name: 'Euro',               symbol: '€',  locales: ['es-ES', 'fr-FR', 'de-DE', 'it-IT'] },
  GBP: { code: 'GBP', name: 'British Pound',      symbol: '£',  locales: ['en-GB'] },
  JPY: { code: 'JPY', name: 'Japanese Yen',       symbol: '¥',  locales: ['ja-JP'] },
  CNY: { code: 'CNY', name: 'Chinese Yuan',       symbol: '¥',  locales: ['zh-CN'] },
  CAD: { code: 'CAD', name: 'Canadian Dollar',    symbol: 'C$', locales: ['en-US', 'fr-FR'] },
  BRL: { code: 'BRL', name: 'Brazilian Real',     symbol: 'R$', locales: ['pt-BR'] },
  KRW: { code: 'KRW', name: 'South Korean Won',   symbol: '₩',  locales: ['ko-KR'] },
  MXN: { code: 'MXN', name: 'Mexican Peso',       symbol: 'MX$',locales: ['es-MX'] },
  TWD: { code: 'TWD', name: 'New Taiwan Dollar',  symbol: 'NT$',locales: ['zh-TW'] },
  SAR: { code: 'SAR', name: 'Saudi Riyal',        symbol: '﷼',  locales: ['ar-SA'] },
  ILS: { code: 'ILS', name: 'Israeli New Shekel', symbol: '₪',  locales: ['he-IL'] },
  NGN: { code: 'NGN', name: 'Nigerian Naira',     symbol: '₦',  locales: ['ha-NG'] },
  KES: { code: 'KES', name: 'Kenyan Shilling',    symbol: 'KSh',locales: ['sw-KE'] },
  ETB: { code: 'ETB', name: 'Ethiopian Birr',     symbol: 'Br', locales: ['am-ET'] },
};

// Ordered list — controls dropdown display order.
export const CURRENCY_ORDER = [
  'USD', 'EUR', 'GBP', 'JPY', 'CNY', 'CAD', 'BRL', 'KRW',
  'MXN', 'TWD', 'SAR', 'ILS', 'NGN', 'KES', 'ETB',
];

export const DEFAULT_CURRENCY = 'USD';

/**
 * Dropdown options for currency selectors.
 * @returns {Array<{value: string, label: string}>}
 */
export const getCurrencyOptions = () =>
  CURRENCY_ORDER.map((code) => {
    const c = CURRENCIES[code];
    return { value: c.code, label: `${c.code} — ${c.name} (${c.symbol})` };
  });

/**
 * Whether a given ISO 4217 code is one of the supported tenant currencies.
 */
export const isSupportedCurrency = (code) =>
  Boolean(code) && Object.prototype.hasOwnProperty.call(CURRENCIES, String(code).toUpperCase());

/**
 * Format an amount in the tenant's currency.
 * @param {number} amount
 * @param {string} currencyCode ISO 4217
 * @param {string} locale BCP-47 (defaults to en-US)
 */
export const formatCurrency = (amount, currencyCode = DEFAULT_CURRENCY, locale = 'en-US') => {
  const code = isSupportedCurrency(currencyCode) ? String(currencyCode).toUpperCase() : DEFAULT_CURRENCY;
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(Number(amount) || 0);
  } catch {
    const sym = CURRENCIES[code]?.symbol || '';
    return `${sym}${(Number(amount) || 0).toFixed(2)}`;
  }
};
